import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Box, Button, Divider } from "@mui/material";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useClientPageTitle from "../../../contexts/useClientPageTitle";
import useInboxStateContext from "../../../contexts/useInboxStateContext";
import { markMessageAsRead } from "../../../hooks/inboxState/inboxState.api";
import { downloadAllAttachments, downloadAttachment } from "../../../services/attachments";
import { currentMessageSelector } from "../../../store/state/messages/selectors";
import type { Message } from "../../../store/state/messages/types";
import type { AppDispatch } from "../../../store/store";
import MessageContent from "./MessageContent";
import MessageMenu from "./MessageMenu";

const MessageComponent = () => {
  useClientPageTitle("Message");

  const dispatch: AppDispatch = useDispatch();
  const message = useSelector(currentMessageSelector);
  const { ui, setMessageId, watermarkInvestorName } = useInboxStateContext();
  const { sendNotificationError } = useNotificationContext();

  const onDownloadAttachment = useCallback(
    async (messageId: string, attachmentId: string) => {
      try {
        await downloadAttachment(messageId, attachmentId, watermarkInvestorName);
        if (!message?.isRead) {
          await markMessageAsRead(messageId)(dispatch);
        }
      } catch {
        sendNotificationError("The attachment cannot be downloaded");
      }
    },
    [dispatch, watermarkInvestorName, message, sendNotificationError]
  );

  const onDownloadAllAttachments = useCallback(
    async (message: Message) => {
      try {
        await downloadAllAttachments(message, watermarkInvestorName);
        if (!message.isRead) {
          await markMessageAsRead(message.id)(dispatch);
        }
      } catch {
        sendNotificationError("The attachment cannot be downloaded");
      }
    },
    [dispatch, watermarkInvestorName, sendNotificationError]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box sx={{ display: "flex", width: "100%", px: 2, py: 1.5 }}>
        <Button startIcon={<ArrowBackRoundedIcon />} color={"secondary"} onClick={() => setMessageId("")}>
          Back
        </Button>
        <HorizontalFill />
        {!ui.messageDetailLoadingError && <MessageMenu downloadAllAttachments={onDownloadAllAttachments} />}
      </Box>
      <Divider flexItem />
      {ui.messageDetailLoadingError ? (
        <DataLoadingFailed />
      ) : ui.messageDetailLoading || !message ? (
        <InlineLoader />
      ) : (
        <MessageContent downloadAttachment={onDownloadAttachment} />
      )}
    </Box>
  );
};

export default MessageComponent;
